import { useState } from "react";
import { currencyFormatter, pointsFormatter } from "../../helpers";
import { Page, Uid } from "../../types";
import FullscreenModal from "../FullscreenModal";
import styles from "./ArtistModal.module.css";
import { globals, utilities, images } from "../../styles";
import { useNavigate } from "../../hooks/useNavigate";
import { useLineupCache } from "../../hooks/useLineup";
import {
  artist_Icon,
  news_Icon,
  arrow_Icon,
  transfer_Icon,
  plus_Icon,
} from "../../assets/icons";
import { useCurrentDraft } from "../../hooks/drafts";
import { useUserId } from "../../hooks/useUserId";
import {
  useGetArtist,
  useGetArtistPoints,
  useGetArtistPrice,
} from "../../hooks/artists";
import LoadingSpinner from "../LoadingSpinner";
import {
  useArtistRecruitmentPrice,
  useCurrentLineupIndex,
} from "../../hooks/lineups";

function ArtistModal(props: {
  currentArtistId: Uid;
  isCurrentArtistInLineUp: boolean;
  isLineupFull: boolean;
  handleCloseArtistPage: () => void;
  handleAddToLineup?: () => void;
  handleDropFromLineup?: () => void;
  handleBackToSearch?: () => void;
  handleArtistBoost?: () => void;
}) {
  const {
    currentArtistId,
    handleAddToLineup,
    handleBackToSearch,
    handleCloseArtistPage,
    handleDropFromLineup,
    isCurrentArtistInLineUp,
    isLineupFull,
    handleArtistBoost,
  } = props;
  const { data: currentDraft } = useCurrentDraft();
  const userId = useUserId();
  const { currentLineupIndex } = useCurrentLineupIndex();
  const { data: artistInfo, isPending: isArtistInfoLoading } =
    useGetArtist(currentArtistId);
  const { data: artistPrice, isPending: isArtistPriceLoading } =
    useGetArtistPrice(currentArtistId, currentDraft?.draftId);
  const { data: artistPoints, isPending: isArtistPointsLoading } =
    useGetArtistPoints(currentArtistId, {});
  const { data: artistRecruitmentPrice } = useArtistRecruitmentPrice(
    userId,
    currentArtistId,
    {
      draftId: currentDraft?.draftId,
      lineupIndex: currentLineupIndex,
    },
  );
  const { navigate } = useNavigate();

  const { state: lineupState } = useLineupCache(userId, {
    draftId: currentDraft?.draftId,
    lineupIndex: currentLineupIndex,
  });
  const [panelView, setPanelView] = useState<"weekly" | "total">("weekly");

  if (
    !artistInfo ||
    !artistPrice ||
    !artistPoints ||
    isArtistInfoLoading ||
    isArtistPriceLoading ||
    isArtistPointsLoading
  ) {
    return (
      <FullscreenModal
        onClose={handleBackToSearch ?? handleCloseArtistPage}
        className={{
          container: `${styles["artist-modal"]}`,
        }}
      >
        <LoadingSpinner />
      </FullscreenModal>
    );
  }

  const costDifference =
    currentLineupIndex != null && artistRecruitmentPrice?.recruitmentPrice
      ? artistPrice.currentPrice - artistRecruitmentPrice.recruitmentPrice
      : 0;
  const costDifferenceSymbol = costDifference > 0 ? "+" : "";
  const costDifferenceClass =
    costDifference > 0
      ? utilities["text-green"]
      : costDifference < 0
        ? utilities["text-red"]
        : "";
  return (
    <FullscreenModal
      onClose={handleBackToSearch ?? handleCloseArtistPage}
      className={{
        container: `${styles["artist-modal"]}`,
      }}
    >
      <div className={`${styles["artist-overview"]}`}>
        <div className={styles["artist-text"]}>
          <div className={`${globals["artist-name"]}`}>{artistInfo.name}</div>
          <div className={`${globals["artist-genres"]}`}>
            Genre 1 • Genre 2 • Genre 3
          </div>
        </div>
        <div className={styles["artist-image"]}>
          <img
            src={artist_Icon}
            alt={artistInfo.name}
            className={images["artist-icon"]}
          />
        </div>
      </div>
      <div className={styles["artist-panel"]}>
        <div className={styles["artist-panel-header"]}>
          <select
            name="panelView"
            className={styles["artist-panel-select"]}
            value={panelView}
            onChange={(e) => setPanelView(e.target.value as "weekly" | "total")}
          >
            {currentDraft && currentDraft.currentWeek > 0 && (
              <option value="weekly">Last Week</option>
            )}
            <option value="total">Total</option>
          </select>
        </div>
        <div className={styles["artist-panel-body"]}>
          <div className={styles["artist-panel-divider"]} />
          <div className={styles["artist-panel-row"]}>
            <div>Streams</div>
            <div>
              {pointsFormatter(
                panelView === "weekly"
                  ? artistPoints.lastWeek.streamingPoints
                  : artistPoints.streamingPoints,
              )}
            </div>
          </div>
          <div className={styles["artist-panel-row"]}>
            <div>Social Media</div>
            <div>
              {pointsFormatter(
                panelView === "weekly"
                  ? artistPoints.lastWeek.socialPoints
                  : artistPoints.socialPoints,
              )}
            </div>
          </div>
          <div className={styles["artist-panel-row"]}>
            <div>Live Shows</div>
            <div>
              {pointsFormatter(
                panelView === "weekly"
                  ? artistPoints.lastWeek.showPoints
                  : artistPoints.showPoints,
              )}
            </div>
          </div>
          <div className={styles["artist-panel-divider"]} />
          {panelView === "weekly" ? (
            <>
              <div className={styles["artist-panel-row"]}>
                <div>Points earned last week</div>
                <div>{pointsFormatter(artistPoints.lastWeek.totalPoints)}</div>
              </div>
              <div className={styles["artist-panel-row"]}>
                <div> Average Points Per Week</div>
                <div>
                  {pointsFormatter(artistPoints.perWeekAverage.totalPoints)}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className={styles["artist-panel-row"]}>
                <div>Lifetime Points Earned</div>
                <div>{pointsFormatter(artistPoints.totalPoints)}</div>
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles["artist-panel"]}>
        <div className={styles["artist-cost-header"]}>
          <div>Artist Costs</div>
        </div>
        <div className={styles["artist-cost-body"]}>
          <div className={styles["artist-panel-divider"]} />
          <div className={styles["artist-panel-row"]}>
            <div>Current</div>
            <div>{currencyFormatter(artistPrice.currentPrice)}</div>
          </div>
          <div className={styles["artist-panel-row"]}>
            <div>Highest</div>
            <div>{currencyFormatter(artistPrice.highestPrice)}</div>
          </div>
          <div className={styles["artist-panel-row"]}>
            <div>Lowest</div>
            <div>
              {currencyFormatter(
                artistPrice.lowestPrice ?? artistPrice.currentPrice,
              )}
            </div>
          </div>
          <div className={styles["artist-panel-divider"]} />
          <div
            className={styles["artist-panel-note"]}
          >{`${isCurrentArtistInLineUp ? "In" : "Not in"} next week's lineup.`}</div>
          {costDifference > 0 && artistRecruitmentPrice && (
            <div className={styles["artist-panel-row"]}>
              <div>
                Player Paid:{" "}
                {currencyFormatter(artistRecruitmentPrice.recruitmentPrice)}
              </div>
              <div
                className={costDifferenceClass}
              >{`${costDifferenceSymbol}${currencyFormatter(costDifference)}`}</div>
            </div>
          )}
        </div>
      </div>
      <button
        onClick={() => {
          navigate(Page.News);
          handleCloseArtistPage();
        }}
        className={styles["news-btn"]}
      >
        <div className={styles["news-btn-icon"]}>
          <img src={news_Icon} alt="news" className={images["artist-icon"]} />
        </div>
        <span className={styles["news-btn-label"]}>{artistInfo.name} News</span>
        <img src={arrow_Icon} alt="go" className={styles["news-btn-arrow"]} />
      </button>
      {/* TODO: re-add shows ui <div className={styles["shows-carousel"]}>
        <div className={styles["shows-carousel-title"]}>Upcoming Shows</div>
        <div className={styles["shows-carousel-track"]}>
          {DUMMY_SHOWS.map((show, i) => (
            <div key={i} className={styles["show-card"]}>
              <div className={styles["show-card-date"]}>
                <span className={styles["show-card-month"]}>{show.month}</span>
                <span className={styles["show-card-day"]}>{show.day}</span>
                <span className={styles["show-card-city"]}>{show.city}</span>
              </div>
              <div className={styles["show-card-venue"]}>{show.venue}</div>
              <div className={styles["show-card-capacity"]}>
                Capacity: {show.capacity}
              </div>
            </div>
          ))}
        </div>
      </div> */}
      <div className={utilities["flex-col"]}>
        {handleDropFromLineup && handleAddToLineup && (
          <>
            {isCurrentArtistInLineUp ? (
              <div
                className={`${utilities["flex-row"]} ${utilities["flex-gap"]}`}
              >
                <button
                  onClick={handleDropFromLineup}
                  className={globals["lrg-btn"]}
                >
                  Drop from Lineup
                </button>
                {!!handleArtistBoost && (
                  <button
                    onClick={handleArtistBoost}
                    className={globals["lrg-btn"]}
                  >
                    Boost Artist
                  </button>
                )}
              </div>
            ) : (
              // TODO: Allow to be pressed if lineup is full, but show a modal asking to
              //  drop an artist.
              <button
                onClick={handleAddToLineup}
                className={globals["lrg-btn"]}
                disabled={isLineupFull}
              >
                <span className={styles["lineup-btn-icons"]}>
                  <img
                    src={plus_Icon}
                    alt=""
                    className={images["lineup-btn-plus-icon"]}
                  />
                  <img
                    src={artist_Icon}
                    alt=""
                    className={images["lineup-btn-artist-icon"]}
                  />
                </span>
                Add to Lineup
              </button>
            )}
          </>
        )}
        <div className={utilities["flex-col"]}>
          <span className={styles["info-span"]}>
            <img
              src={transfer_Icon}
              alt="transfers"
              className={images["transfer-inline-icon"]}
            />{" "}
            Free transfers available: {lineupState?.trades ?? 0}
          </span>
          <span
            className={`${styles["info-span"]} ${styles["info-span--caprasimo"]}`}
          >
            Available money: {currencyFormatter(lineupState?.money ?? 0)}
          </span>
        </div>
      </div>
      <div className={`${utilities["flex-row"]} ${utilities["flex-gap"]}`}>
        <button onClick={handleCloseArtistPage} className={globals["main-btn"]}>
          Done
        </button>
      </div>
    </FullscreenModal>
  );
}

export default ArtistModal;
